/**
 * A player in the air (spec 02 §3 Fall, Takeoff drift, Air steering), ported from rebb64: the jump's
 * horizontal part D_2483 / L248D, the every-2nd-tick checks D_25F1 (fire, then steering), and the fall
 * D_257C with its one-way landing test.
 */

import { addScore } from '../score';
import { tryBlow } from './blow';
import { probe, solidAt, solidRun, type Probe } from './probe';
import { JOY_FIRE, JOY_LEFT, JOY_RIGHT } from './state';
import {
  AIR_CHECK_TICKS, FALL_STEP, GRID_Y, LAND_MIN_Y_FALL, RING_SCORE, WRAP_BOTTOM, WRAP_TOP, X_MAX, X_MIN,
} from './tuning';
import type { Player, World } from '../types';

/** The player has come down on a platform. */
export function landed(world: World, p: Player): void {
  world.events.push({ type: 'land', player: p.id });
}

/** D_2483: a tick of a jump in the air: the purple ring's points, a fresh probe, then the drift. */
export function airMove(world: World, p: Player, frame0: number): void {
  if (p.motion.purpleRing) addScore(world, p.id, RING_SCORE);
  airDrift(world, p, probe(p.x, p.y), frame0);
}

/**
 * L248D: the takeoff drift, 1 px every tick of the jump. The X limits stop it for good, and so does a
 * solid tile beside the lower body - unless the body is inside a solid tile itself (rising through a
 * platform), when it carries on (L24A0). Then L24FA: every 2nd tick, unless blowing, the frame's step
 * toggles and D_25F1 runs.
 */
export function airDrift(world: World, p: Player, pr: Probe, frame0: number): void {
  const m = p.motion;
  if (m.driftLeft) {
    if (p.x < X_MIN + 1) {
      p.x = X_MIN;
      m.driftLeft = false;
    } else if (driftWall(world, p, pr, pr.fy !== 0 ? 0x50 : 0x28, 1)) m.driftLeft = false;
    else p.x--;
  } else if (m.driftRight) {
    if (p.x >= X_MAX) {
      p.x = X_MAX;
      m.driftRight = false;
    } else if (driftWall(world, p, pr, pr.fy !== 0 ? 0x53 : 0x2b, -1)) m.driftRight = false;
    else p.x++;
  }
  m.anim = (m.anim + 1) & 0xff;
  if (m.anim < AIR_CHECK_TICKS) return;
  m.anim = 0;
  if (m.frame >= 8) return;
  m.frame ^= 1;
  steer(world, p, frame0);
}

/** L24A0 / L24D8: the drift stops at a wall tile beside the body when the tile toward the body is clear. */
function driftWall(world: World, p: Player, pr: Probe, off: number, inward: number): boolean {
  if (p.y < GRID_Y || pr.fx !== 0) return false;
  return solidAt(world, pr, off) && !solidAt(world, pr, off + inward);
}

/**
 * D_25F1, every 2nd tick in the air: fire (D_22E8), then left or right. Facing away from the held
 * direction (by the tick-start frame), the check only turns the player, and not while blowing; facing
 * it, the player moves 1 px unless a solid tile is beside the lower body (L261E / L265D).
 */
export function steer(world: World, p: Player, frame0: number): void {
  const m = p.motion;
  const pr = probe(p.x, p.y);
  if (m.joy & JOY_FIRE) tryBlow(world, p);
  const facesRight = frame0 < 4 || frame0 === 8 || frame0 === 9;
  if (m.joy & JOY_LEFT) {
    if (facesRight) {
      if (m.blow < 0) m.frame = 4;
    } else if (p.x !== X_MIN && steerClear(world, p, pr, 0x28, 0x50)) p.x--;
    return;
  }
  if ((m.joy & JOY_RIGHT) === 0) return;
  if (!facesRight) {
    if (m.blow < 0) m.frame = 0;
  } else if (p.x !== X_MAX && steerClear(world, p, pr, 0x2b, 0x53)) p.x++;
}

/** L261E: only a body on the column grid is stopped, by the tile beside its lower body. */
function steerClear(world: World, p: Player, pr: Probe, off: number, offLow: number): boolean {
  if (pr.fy !== 0 && p.y < GRID_Y) return true;
  return pr.fx !== 0 || !solidAt(world, pr, pr.fy !== 0 ? offLow : off);
}

/**
 * D_257C: falling 2 px per tick. Each time Y reaches an 8-px line (from $26 down), using the probe
 * taken before the move: a solid tile beside the lower body lets it through; otherwise a solid tile
 * under the feet lands it, X rounded to even toward its facing (L25BF). Y $F5 wraps to $15.
 * Every 2nd tick, D_25F1.
 */
export function fallTick(world: World, p: Player, pr: Probe, frame0: number): void {
  const m = p.motion;
  const y = (p.y + FALL_STEP) & 0xff;
  if (y === WRAP_BOTTOM) {
    p.y = WRAP_TOP;
    return;
  }
  p.y = y;
  if (y >= LAND_MIN_Y_FALL && ((y - GRID_Y) & 7) === 0 && !solidRun(world, pr, 0x51) && solidRun(world, pr, 0x79)) {
    m.falling = false;
    const up = m.frame < 4 || m.frame === 8 || m.frame === 9;
    p.x = up ? (p.x + 1) & 0xfe : p.x & 0xfe;
    landed(world, p);
    return;
  }
  m.anim = (m.anim + 1) & 0xff;
  if (m.anim < AIR_CHECK_TICKS) return;
  m.anim = 0;
  steer(world, p, frame0);
}
